import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import classNames from "classnames";

import type {
  SiftedColorType,
  ContentSubscriptionLevelType,
  ReaderSubscriptionLevelType,
} from "lib/types/rankings";
import { customColors, isValidSectorColor } from "lib/colors";
import { LockIcon, UnlockIcon } from "components/icons/icons";

export type ContentLabelProps = {
  contentSubscriptionLevel: ContentSubscriptionLevelType;
  colorClass?: SiftedColorType;
  variant?: "small" | "large" | "noText";
};

const getLabelText = (
  contentSubscriptionLevel: ContentSubscriptionLevelType
): string | null => {
  switch (contentSubscriptionLevel) {
    case "is_member":
      return "PLUS";
    case "is_pro":
      return "PRO";
    default:
      return null;
  }
};

const isLocked = ({
  contentSubscriptionLevel,
  readerSubscriptionLevel,
}: {
  contentSubscriptionLevel: ContentSubscriptionLevelType;
  readerSubscriptionLevel: ReaderSubscriptionLevelType;
}): boolean => {
  if (
    readerSubscriptionLevel === "mem" &&
    contentSubscriptionLevel === "is_pro"
  ) {
    return true;
  }

  if (
    readerSubscriptionLevel === "reg" &&
    contentSubscriptionLevel !== "is_free"
  ) {
    return true;
  }

  if (!readerSubscriptionLevel) {
    return true;
  }

  return false;
};

const ContentLabel = ({
  contentSubscriptionLevel,
  colorClass = "cerise",
  variant = "large",
}: ContentLabelProps) => {
  const [cookies] = useCookies(["sMemLev"]);
  const [locked, setLocked] = useState(true);

  useEffect(() => {
    const readerSubscriptionLevel = cookies["sMemLev"];
    setLocked(isLocked({ contentSubscriptionLevel, readerSubscriptionLevel }));
  }, [cookies, contentSubscriptionLevel]);

  const backgroundColor = isValidSectorColor(colorClass)
    ? colorClass
    : "cerise";

  const outerClasses = classNames(
    "inline-block text-white font-bold uppercase tracking-widest text-3xs",
    {
      "px-5 py-sm": variant === "large",
      "p-1 leading-none": variant === "small",
      "px-[10px] py-[8px] lg:px-[13px] lg:py-[10px] rounded-md":
        variant === "noText",
    }
  );

  const isFreeContent = contentSubscriptionLevel === "is_free";
  // No labelling should be shown for free content
  if (isFreeContent) {
    return null;
  }

  return (
    <div
      className={outerClasses}
      style={{
        backgroundColor: customColors[backgroundColor],
      }}
      data-testid="content-label"
    >
      <div className="flex w-full items-center justify-center">
        {locked ? <LockIcon /> : <UnlockIcon />}
        {variant === "noText" ? null : (
          <span className="ml-2 mt-px leading-none">
            {getLabelText(contentSubscriptionLevel)}
          </span>
        )}
      </div>
    </div>
  );
};

export default ContentLabel;
